/**
 * Mock Backend API for Offline Development
 *
 * Complete mock backend implementation that simulates the entire backend system.
 * This allows full frontend development without any backend connection.
 *
 * WHY THIS EXISTS:
 * - Backend handles OTP bypass with dev env codes
 * - Frontend still needs complete mock for offline development
 * - Enables UI/UX work without any backend/database/SMS dependencies
 *
 * To enable: Set NEXT_PUBLIC_USE_BACKEND_MOCK=true in .env.development
 */

import { logger } from '@/lib/logger'
import type {
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  LoginResponse,
  User,
} from '@/types/api'
import { MOCK_USERS, getMockUserByPhone } from '@/mocks/users'

// Store OTP codes in memory for testing
const otpStore = new Map<string, { code: string; expiresAt: number }>()

// Mock user database - initialized with pre-configured mock users
const mockUserDatabase = new Map<string, User>(
  Object.values(MOCK_USERS)
    .filter(
      (mockUser): mockUser is typeof mockUser & { phone: string } =>
        mockUser.phone != null
    )
    .map((mockUser) => [
      mockUser.phone,
      {
        id: mockUser.id,
        phone: mockUser.phone,
        email: mockUser.email ?? undefined,
        role: mockUser.role,
        lastLoginAt: mockUser.lastLoginAt ?? undefined,
        createdAt: mockUser.createdAt,
        updatedAt: mockUser.updatedAt,
      } as User,
    ])
)

// Mock token storage (simulates JWT)
const mockTokens = new Map<
  string,
  { accessToken: string; refreshToken: string; userId: string }
>()

// Mock revoked tokens (for logout)
const revokedTokens = new Set<string>()

// Generate mock JWT-like token
function generateMockToken(userId: string, type: 'access' | 'refresh'): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2)
  return `mock_${type}_${userId}_${timestamp}_${random}`
}

// Find user by token
function getUserByToken(token: string): User | undefined {
  for (const [userId, tokens] of mockTokens.entries()) {
    if (tokens.accessToken === token || tokens.refreshToken === token) {
      // Find user by ID
      for (const user of mockUserDatabase.values()) {
        if (user.id === userId) {
          return user
        }
      }
    }
  }
  return undefined
}

/**
 * Mock: Send OTP to phone number
 * Supports both pre-configured mock users (with custom OTPs) and new users (default OTP)
 */
export async function sendOTP(data: SendOTPRequest): Promise<SendOTPResponse> {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 800))

  // Get mock user or use default OTP
  const mockUser = getMockUserByPhone(data.phone)
  const otp = mockUser?.otp || '000000'
  const expiresIn = 300 // 5 minutes

  // Store OTP
  otpStore.set(data.phone, {
    code: otp,
    expiresAt: Date.now() + expiresIn * 1000,
  })

  return {
    success: true,
    expiresIn,
  }
}

/**
 * Mock: Verify OTP and login
 * Returns full user data with tokens for any matching test account or creates new user
 */
export async function verifyOTP(
  data: VerifyOTPRequest
): Promise<LoginResponse> {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 800))

  // Check if OTP exists
  const storedOTP = otpStore.get(data.phone)
  if (!storedOTP) {
    throw new Error('No OTP request found. Please request a new code.')
  }

  // Check if expired
  if (Date.now() > storedOTP.expiresAt) {
    otpStore.delete(data.phone)
    throw new Error('OTP code has expired. Please request a new code.')
  }

  // Verify code
  if (data.otp !== storedOTP.code) {
    throw new Error('Invalid verification code. Please try again.')
  }

  // Success! Clear OTP
  otpStore.delete(data.phone)

  // Get or create mock user
  let user = mockUserDatabase.get(data.phone)

  if (!user) {
    // Create new user for this phone (default role: USER)
    const newUser: User = {
      id: `mock-user-${Date.now()}`,
      phone: data.phone,
      email: null,
      role: 1, // USER
      isNewUser: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    mockUserDatabase.set(data.phone, newUser)
    user = newUser
  } else {
    // Update lastLoginAt for existing user
    user.lastLoginAt = new Date().toISOString()
    user.updatedAt = new Date().toISOString()
    user.isNewUser = false
  }

  // Generate mock tokens
  const accessToken = generateMockToken(user.id, 'access')
  const refreshToken = generateMockToken(user.id, 'refresh')

  // Store tokens for later refresh
  mockTokens.set(user.id, { accessToken, refreshToken, userId: user.id })

  // Create mock settings based on user preferences sent in request
  const settings = {
    language: data.language || 'en',
    theme: data.theme || 'light',
    timezone: data.timezone || 'UTC',
    notificationEmail: true,
    notificationSms: false,
    emailMarketingOptIn: false,
    emailMarketingOptInTimestamp: null,
    smsMarketingOptIn: false,
    smsMarketingOptInTimestamp: null,
    optInMethod: null,
  }

  // Return wrapped response to match real backend format
  // Note: refreshToken only included for mobile (platform !== 'web')
  const responseData: {
    user: User
    settings: typeof settings
    accessToken: string
    refreshToken?: string
  } = {
    user,
    settings,
    accessToken,
  }

  // Only include refreshToken for non-web platforms (matches backend behavior)
  if (data.platform !== 'web') {
    responseData.refreshToken = refreshToken
  }

  return {
    success: true,
    message: 'Authentication successful',
    data: responseData,
  }
}

/**
 * Mock: Refresh access token
 * Takes a refresh token and returns a new access token
 */
export async function refreshToken(
  oldRefreshToken: string
): Promise<{ accessToken: string }> {
  logger.log('🔄 [MOCK API] Refreshing token...')

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500))

  // Check if token is revoked
  if (revokedTokens.has(oldRefreshToken)) {
    logger.error('❌ [MOCK API] Token has been revoked')
    throw new Error('Refresh token has been revoked. Please login again.')
  }

  // Find user by refresh token
  const user = getUserByToken(oldRefreshToken)
  if (!user) {
    logger.error('❌ [MOCK API] Invalid refresh token')
    throw new Error('Invalid refresh token. Please login again.')
  }

  // Generate new access token
  const newAccessToken = generateMockToken(user.id, 'access')

  // Update stored tokens
  const storedTokens = mockTokens.get(user.id)
  if (storedTokens) {
    storedTokens.accessToken = newAccessToken
  }

  return {
    accessToken: newAccessToken,
  }
}

/**
 * Mock: Logout
 * Revokes refresh token and clears user session
 */
export async function logout(
  refreshToken?: string,
  logoutAll?: boolean
): Promise<void> {
  logger.log('👋 [MOCK API] Logging out...')

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 300))

  if (refreshToken) {
    if (logoutAll) {
      // Find user and revoke all their tokens
      const user = getUserByToken(refreshToken)
      if (user) {
        mockTokens.delete(user.id)
        logger.log('✅ [MOCK API] Logged out from all devices')
      }
    } else {
      // Just revoke this specific refresh token
      revokedTokens.add(refreshToken)
      logger.log('✅ [MOCK API] Logged out from this device')
    }
  } else {
    logger.log('✅ [MOCK API] Logout acknowledged (no token provided)')
  }
}

/**
 * Mock: Get current user
 * Returns user data based on access token
 */
export async function getCurrentUser(accessToken: string): Promise<User> {
  logger.log('👤 [MOCK API] Getting current user...')

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 300))

  // Check if token is revoked
  if (revokedTokens.has(accessToken)) {
    logger.error('❌ [MOCK API] Token has been revoked')
    throw new Error('Access token has been revoked. Please login again.')
  }

  // Find user by access token
  const user = getUserByToken(accessToken)
  if (!user) {
    logger.error('❌ [MOCK API] Invalid access token')
    throw new Error('Invalid access token. Please login again.')
  }

  logger.log('✅ [MOCK API] User found:', user.phone, '(ID:', user.id + ')')
  return user
}

/**
 * Helper: Debug current mock backend state
 */
export function debugMockState() {
  logger.log('📋 [MOCK API] Current State:')
  logger.log('  Users:', mockUserDatabase.size)
  logger.log('  Active OTPs:', otpStore.size)
  logger.log('  Active Sessions:', mockTokens.size)
  logger.log('  Revoked Tokens:', revokedTokens.size)

  logger.log('\n👥 Mock Users:')
  mockUserDatabase.forEach((user) => {
    logger.log(`  ${user.phone} (ID: ${user.id}) - Role: ${user.role}`)
  })

  logger.log('\n🔐 Active OTPs:')
  otpStore.forEach((value, phone) => {
    const isExpired = Date.now() > value.expiresAt
    logger.log(`  ${phone}: ${value.code} ${isExpired ? '(expired)' : ''}`)
  })
}
